import { useEffect, useReducer, useState } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";

import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000GetHandlers } from "../../u2000Core/hooks/handler/U2000UseGetHandlers";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import type { U2000SearchPayload, U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, U2000FormStateEnumState, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { NS, U3142ROLES } from "../U3142";
import U3142LotBody from "./form/U3142LotBody";
import { U3142LotFormToolBar } from "./form/U3142LotFormToolBar";
import { U3142LotDocumentsTab } from "./form/U3142LotDocumentsTab";
import U3142LotDeleteDlg from "./common/U3142LotDeleteDlg";
import { useU3142Context } from "../U3142Context";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import { doCreateData, doDeleteData, doGetAuditsData, doGetData, doSearch, doUpdateData, emptyLotDetail, luHandleAppiError, type U3142LotDetail } from "../U3142Service";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import { generateTemporaryId } from "../../u2000Core/tools/U2000IdUtils";
import { validateConcordanceEntries } from "../../u3141LandRegister/components/form/u3141ChildLotChain/U3141ConcordanceUtils";

// Interface à placer en dehors du composant pour éviter l'erreur de modificateur
export interface U3142LotFormExtendedHandlers extends U2000FormStateHandlersReturn<U3142LotDetail> {
  addNewLot: () => void;
  modifyLot: () => void;
  deleteLot: () => void;
}

const U3142LotForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  // Form state and reducer
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  // manage active tab state
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // navigate et location
  const location = useLocation();
  const navigate = useNavigate();

  // Translation
  const { t } = useTranslation();
  const toast = useToast();

  // API Axios Client
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  // LU Handler,  Search Handlers, Grid Handlers from Context
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3142Context();

  // Form Handlers and default methods
  const validateForm = (lot: U3142LotDetail) => {
    const result: Record<string, string> = {};
    if (!lot.lot || lot.lot.trim() === "") {
      result["lot"] = t("errLotRequired", { ns: NS });
    }

    if (!lot.district || !lot.district.id) {
      result["district.id"] = t("errDistrictRequired", { ns: NS });
    }

    if (!lot.cadastre || !lot.cadastre.id) {
      result["cadastre.id"] = t("errCadastreRequired", { ns: NS });
    }

    if (!lot.dateEstablishment) {
      result["dateEstablishment"] = t("errDateEstablishmentRequired", { ns: NS });
    }

    if (!lot.operationType || lot.operationType.trim() === "") {
      result["operationType"] = t("errOperationTypeRequired", { ns: NS });
    }

    if (!lot.lotState || lot.lotState.trim() === "") {
      result["lotState"] = t("errLotStateRequired", { ns: NS });
    }

    const concordanceValidation = validateConcordanceEntries(lot.concordance ?? []);
    if (!concordanceValidation.valid && concordanceValidation.errors?.length) {
      result["concordance"] = concordanceValidation.errors[0] ?? t("errConcordanceInvalid", { ns: NS });
    }
    return result;
  };

  const u3142LotBL: U2000FormSBusinessHandlers<U3142LotDetail> = {
    newRecord: () => {
      const lot = { ...emptyLotDetail };
      lot.id = generateTemporaryId();
      return lot;
    },

    // validation du formulaire
    validateForm: validateForm,

    // sauvegarde du formulaire
    saveForme: async (lot: U3142LotDetail, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(lot);
      if (Object.keys(errors).length === 0) {
        handleSave.saveData(lot, editMode);
      } else {
        u3142LotFormStateExtendedHandlers.setErrors(errors);
      }
    },

    // annulation du formulaire
    cancelForm: () => {
      const lot = u3142LotFormStateExtendedHandlers.getOldData() as U3142LotDetail | null;
      if (lot) {
        u3142LotFormStateExtendedHandlers.setData({ ...lot });
      }
      u3142LotFormStateExtendedHandlers.setCustomAttributes({});

      if (luStateHandlers.getActionOriginFrom() === "/U3142") {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },

    // gestion du changement d'enregistrement
    onChangeRecordForm: (lot) => {
      if (lot.id) {
        handleGet.getData(lot.id);
      }
    },

    // gestion du changement de page
    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },

    // gestion de la sortie du formulaire
    onLeaveForm: () => {
      navigate("/U3142");
    },
  };

  const u3142LotFormStateHandlers = useU2000FormStateHandlers<U3142LotDetail>({
    formState,
    formStateDispatch,
    businessHandlers: u3142LotBL,
  });

  // extended methods
  const u3142LotFormStateExtendedHandlers: U3142LotFormExtendedHandlers = {
    ...u3142LotFormStateHandlers,

    addNewLot: () => {
      u3142LotFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    },
    modifyLot: () => {
      u3142LotFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteLot: async () => {
      handleDelete.handleDelete();
    },
  };

  // Call Search Handlers for Logical Unit
  const handleSearch = useU2000SearchHandlers<U3142LotDetail>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3142LotDetail>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Get Handlers for Logical Unit
  const handleGet = useU2000GetHandlers<U3142LotDetail & { id: string }>({
    doGetFn: (id: string) => {
      return doGetData(u2000AxiosAuthenticated, id);
    },
    onSuccess: (lot: U3142LotDetail & { id: string }) => {
      gridStateHandlers.setDataById(lot.id, lot);
      u3142LotFormStateExtendedHandlers.setData(lot);

      // Remplace l'id dans l'URL par le nouvel id
      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(lot.id));
      navigate(newPath, { replace: true });
      setLogicalUnitBreadcrumb([{ href: "/U3142", label: t("title", { ns: NS }) }, { label: lot.lot }]);

      // Détermine le mode du formulaire selon l'action précédente
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3142LotFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Save Handlers for Logical Unit (documents gérés via onglet Documents, ne pas envoyer document)
  const handleSave = useU2000SaveHandlers<U3142LotDetail>({
    doSaveFn: (lot: U3142LotDetail, editMode: U2000FormStateEnumEditMode) => {
      const payload = { ...lot } as Record<string, unknown>;
      delete payload.document;
      const cleanLot = payload as unknown as U3142LotDetail;
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        cleanLot.partieDeLot = cleanLot.partieDeLot === true;
        return doCreateData(u2000AxiosAuthenticated, cleanLot);
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, cleanLot.id as string, cleanLot);
      }
      // Ensure a Promise is always returned to satisfy the handler's expected signature
      else return Promise.resolve(undefined);
    },
    onSuccess: (lot: U3142LotDetail & { id: string }, editMode: U2000FormStateEnumEditMode) => {
      u3142LotFormStateExtendedHandlers.setCustomAttributes({});
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), lot]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        gridStateHandlers.setCurrentIdx(gridStateHandlers.getData().length);

        // Remplace l'id dans l'URL par le nouvel id
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(lot.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(lot.id, lot);
      }
      u3142LotFormStateExtendedHandlers.afterSaveData(lot);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));

      // Retourne à la liste après l'ajout ou la modification selon l'origine
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === "/U3142") {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);

      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3142LotFormStateExtendedHandlers.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3142LotFormStateExtendedHandlers.setErrors(errorResult.fieldErrors);
      }
    },
  });

  // Call Delete Handlers for Logical Unit
  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [u3142LotFormStateExtendedHandlers.getData()],
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, row);
    },

    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      let currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: row["lot"], ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      // Apres suppression, aller au prochain record, aller vers l'avant si possible, sinon aller vers l'arrière, si aucun, retourner à la liste
      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.PREVIOUSPAGE);
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate("/U3142", { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  // Initialisation au montage et quand id change (ex. F5 sur /U3142/:id : id peut arriver après le 1er rendu).
  useEffect(() => {
    if (id === undefined) return;
    if (id === "new") {
      u3142LotFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
      u3142LotFormStateExtendedHandlers.setData({ ...emptyLotDetail });
    } else {
      handleGet.getData(id);
    }
  }, [id]);

  // Tabs du formulaire (Documents uniquement si lot existant, pas en création)
  const formTabsComponent: any[] = [
    id && id !== "new" && {
      id: "tabDocuments",
      label: t("tabDocuments", { ns: NS }),
      content: (
        <U3142LotDocumentsTab
          lotId={id}
          lotDetail={u3142LotFormStateExtendedHandlers.getData() ?? null}
          onDocumentsChange={() => handleGet.getData(id)}
          disableDocumentActions={u3142LotFormStateExtendedHandlers.getFormState?.() === U2000FormStateEnumState.EDIT}
        />
      ),
    },
    hasRole(U3142ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: <U2000AuditPanelComponent id={u3142LotFormStateExtendedHandlers.getData()?.id} doGetAuditsData={doGetAuditsData} toastName="U3142LotAudit" />,
    },
  ].filter(Boolean);

  if (u3142LotFormStateExtendedHandlers.getData() === null) {
    return null;
  }

  // Render
  return (
    <>
      <U3142LotDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />

      <U2000DefaultForm
        actionsBtns={<U3142LotFormToolBar formStateHandlers={u3142LotFormStateExtendedHandlers as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3142LotFormStateExtendedHandlers as any}
      >
        <U3142LotBody formStateHandlers={u3142LotFormStateExtendedHandlers as any} t={t} />

        {formTabsComponent.length > 0 && (
          <U2000Tab
            className="mt-4"
            tabs={formTabsComponent}
            defaultTab={formTabsComponent[0]?.id}
            activeTab={activeTab ?? formTabsComponent[0]?.id}
            setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))}
          />
        )}
      </U2000DefaultForm>
    </>
  );
};

export default U3142LotForm;
